import { apiClient } from '@/api/client'
import {
  companyBonusesPath,
  companyBonusApprovePath,
  companyBonusReversePath,
  BONUSES,
  BONUSES_ME,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { PaginatedResponse } from '@/types/common.types'
import type {
  BonusEntry,
  CreateBonusDto,
  BonusListQuery,
  MyBonusListQuery,
  MyBonusListResponse,
} from '@/features/bonuses/types/bonus.types'

export async function listBonuses(
  companyId: string,
  params: BonusListQuery,
): Promise<PaginatedResponse<BonusEntry>> {
  const { data } = await apiClient.get<ApiResponseWrapper<PaginatedResponse<BonusEntry>>>(
    companyBonusesPath(companyId),
    { params },
  )
  return data.data
}

export async function createBonus(
  companyId: string,
  body: CreateBonusDto,
): Promise<BonusEntry> {
  const { data } = await apiClient.post<ApiResponseWrapper<BonusEntry>>(
    companyBonusesPath(companyId),
    body,
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid create bonus response')
  return doc
}

export async function approveBonus(companyId: string, id: string): Promise<BonusEntry> {
  const { data } = await apiClient.post<ApiResponseWrapper<BonusEntry>>(
    companyBonusApprovePath(companyId, id),
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid approve bonus response')
  return doc
}

export async function reverseBonus(companyId: string, id: string): Promise<BonusEntry> {
  const { data } = await apiClient.post<ApiResponseWrapper<BonusEntry>>(
    companyBonusReversePath(companyId, id),
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid reverse bonus response')
  return doc
}

/** Operator workspace: cross-company bonuses. */
export async function getBonusesWorkspace(
  query?: MyBonusListQuery,
): Promise<MyBonusListResponse> {
  const { companyIds, ...rest } = query ?? {}
  const params: Record<string, unknown> = { ...rest }
  if (companyIds && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  const { data } = await apiClient.get<ApiResponseWrapper<MyBonusListResponse>>(
    BONUSES,
    { params },
  )
  const payload = data?.data
  if (payload && Array.isArray(payload.data)) return payload
  return { data: [], pagination: { total: 0, page: 1, limit: query?.limit ?? 10 } }
}

/** Cross-company self-service bonuses: backend auto-scopes to caller's memberships. */
export async function getMyBonuses(
  query?: MyBonusListQuery,
): Promise<MyBonusListResponse> {
  const { companyIds, ...rest } = query ?? {}
  const params: Record<string, unknown> = { ...rest }
  if (companyIds && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  const { data } = await apiClient.get<ApiResponseWrapper<MyBonusListResponse>>(
    BONUSES_ME,
    { params },
  )
  const payload = data?.data
  if (payload && Array.isArray(payload.data)) return payload
  return { data: [], pagination: { total: 0, page: 1, limit: query?.limit ?? 10 } }
}
